import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { AlertTriangle, Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorText } from "@/lib/api";
import { useAdminLang } from "./context";

export default function QRPage() {
  const { t } = useAdminLang();
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const box = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data.settings);
      setUrl(r.data.settings.public_url || window.location.origin);
    }).catch(() => toast.error(t("toast.loadFailed")));
  }, [t]);

  const valid = /^https?:\/\/[^\s]+$/.test(url);
  const isPreview = /preview\.emergentagent\.com|localhost/.test(url);
  const qrValue = `${url}${url.includes("?") ? "&" : "?"}src=qr`;

  const download = () => {
    const canvas = box.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "wok-town-menu-qr.png";
    a.click();
  };

  const saveUrl = async () => {
    try {
      const r = await api.put("/admin/settings", { ...settings, public_url: url });
      setSettings(r.data.settings);
      toast.success(t("toast.saved"));
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-qr-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">{t("qr.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("qr.subtitle")}</p>
      </div>
      <div className="grid gap-6 md:grid-cols-[1fr_auto]">
        <div className="space-y-4 rounded-2xl border bg-card p-6">
          <div className="space-y-2">
            <Label htmlFor="qr-url">{t("qr.url")}</Label>
            <Input id="qr-url" dir="ltr" value={url} onChange={(e) => setUrl(e.target.value.trim())} data-testid="qr-url-input" />
            <p className="text-xs text-muted-foreground">{t("qr.hint")}</p>
          </div>
          {isPreview && <div className="flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="qr-preview-warning"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t("qr.previewWarn")}</div>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={download} disabled={!valid} data-testid="qr-download-btn"><Download className="h-4 w-4" />{t("qr.download")}</Button>
            <Button variant="secondary" onClick={saveUrl} disabled={!valid || !settings} data-testid="qr-save-url-btn"><Save className="h-4 w-4" />{t("qr.saveUrl")}</Button>
          </div>
        </div>
        <div ref={box} className="rounded-2xl bg-white p-5" data-testid="qr-code">
          {valid && <QRCodeCanvas value={qrValue} size={1024} marginSize={2} fgColor="#121214" style={{ width: 240, height: 240 }} />}
        </div>
      </div>
    </div>
  );
}
